import { useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import { TextInputGhost } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcSearchRegular } from '@kep/kcds-icons';

import { useSearchPopover } from './SearchPopoverProvider';

import type { KeyboardEvent } from 'react';

const SEARCH_INPUT_MAX_LENGTH = 100;

const SearchInput = () => {
  const { t } = useTranslation();
  const { inputValue, setInputValue, popoverProps, focusedIndex, setFocusedIndex, totalItems } =
    useSearchPopover();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { open } = popoverProps;

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const newIndex = focusedIndex < totalItems - 1 ? focusedIndex + 1 : 0;
      setFocusedIndex(newIndex);
      const focusedElement = document.querySelector(`[data-search-item-index="${newIndex}"]`);
      if (focusedElement instanceof HTMLElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const newIndex = focusedIndex > 0 ? focusedIndex - 1 : totalItems - 1;
      setFocusedIndex(newIndex);
      const focusedElement = document.querySelector(`[data-search-item-index="${newIndex}"]`);
      if (focusedElement instanceof HTMLElement) {
        focusedElement.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      const focusedElement = document.querySelector(`[data-search-item-index="${focusedIndex}"]`);
      if (focusedElement instanceof HTMLElement) {
        focusedElement.click();
      }
    }
  };

  const handleBlur = () => {
    setFocusedIndex(-1);
  };

  return (
    <StyledTextField
      inputRef={inputRef}
      size={'lg'}
      value={inputValue}
      onChange={e => setInputValue(e.target.value)}
      onClear={() => setInputValue('')}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      placeholder={t('layout:search.input.placeholder')}
      startAdornment={<IcSearchRegular />}
      slotProps={{ input: { maxLength: SEARCH_INPUT_MAX_LENGTH } }}
    />
  );
};

const StyledTextField = styled(TextInputGhost)(() => ({
  width: '100%',
  height: 48,
  padding: '0 16px',
  backgroundColor: 'transparent !important',

  '& .MuiInputBase-input': {
    paddingLeft: 0,
  },

  '& .MuiInputAdornment-root svg': {
    width: 16,
    height: 16,
    color: '#676F7D',
  },

  '&::before, &::after': {
    borderBottom: 'none !important',
  },
}));

export default SearchInput;
